import { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { RequireSpace } from '@/components/common/RequireSpace';
import { RequireRole } from '@/components/common/RequireRole';
import { DialerProvider } from '@/contexts/DialerContext';
import DebugErrorBoundary from '@/lib/DebugErrorBoundary';
import DialerLayout from '@/layouts/DialerLayout';
import SuperAdminLayout from '@/layouts/SuperAdminLayout';
import { Button } from '@/components/ui/button';
import { toUserMessage } from '@/lib/errorToast';
import { ApiErrorException } from '@/api/client';
import { openOrganisationTeam } from '@/lib/openOrganisationTeam';

export { openOrganisationTeam };

/**
 * DUP-3 (W6): one Team page, `/team`, in the shell of whoever opens it.
 *
 *   - a member of an organisation: the dialer shell, as before;
 *   - the super admin: the back-office shell. They reach it from an
 *     organisation row, which first makes that organisation the active one.
 *
 * `/TeamManagement` (the former back-office entry) and
 * `/organisations/:id/members` (OrganisationMembers, deleted) redirect here
 * (DUP-7), so old links and the OAuth callbacks keep working.
 */
export default function TeamShell() {
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin === true);
  if (isSuperAdmin) {
    return <RequireRole roles={[]}><SuperAdminLayout /></RequireRole>;
  }
  return (
    <RequireSpace space="dialer">
      <DebugErrorBoundary><DialerProvider><DialerLayout /></DialerProvider></DebugErrorBoundary>
    </RequireSpace>
  );
}

/** `/TeamManagement?…` → `/team?…`: the query string carries the OAuth callback results. */
export function TeamManagementRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/team${search}`} replace />;
}

const OPEN_TEAM_FAILED_CODES = new Set(['SWITCH_FORBIDDEN', 'ORGANISATION_NOT_FOUND', 'MEMBERSHIP_ORGANISATION_MISMATCH']);

/** `/organisations/:organisationId/members` (OrganisationMembers, deleted in W6). */
export function OrganisationMembersRedirect() {
  const { organisationId } = useParams();
  const { t } = useTranslation(['admin']);
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin === true);
  const active = useAuthStore((s) => s.activeMembership);
  const switchOrganisation = useAuthStore((s) => s.switchOrganisation);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin || !organisationId || done) return;
    setDone(true);
    openOrganisationTeam(organisationId, {
      activeOrganisationId: active?.organisationId,
      switchOrganisation,
      navigate: (to) => window.location.assign(to),
    }).catch(setError);
  }, [isSuperAdmin, organisationId, active?.organisationId, switchOrganisation, done]);

  // A tenant admin only ever manages their own organisation: its team.
  if (!isSuperAdmin) return <Navigate to="/team" replace />;
  if (error) {
    // QA-W6-7: an unknown or forbidden organisation reads as such, translated,
    // never the server's English message; the code stays below (DES-16).
    const code = error instanceof ApiErrorException ? error.code : undefined;
    const message = code && OPEN_TEAM_FAILED_CODES.has(code) ? t('admin:organisations.openTeamFailed') : toUserMessage(error);
    return (
      <div role="alert" className="p-6 space-y-3">
        <p className="text-sm">{message}</p>
        {error instanceof ApiErrorException && <p className="font-mono text-[11px] text-muted-foreground">{error.code}</p>}
        <Button asChild variant="outline" size="sm"><a href="/AdminOrganisations">{t('admin:superAdminLayout.nav.organisations')}</a></Button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center p-10" aria-busy="true">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-slate-800 motion-reduce:animate-none" />
    </div>
  );
}
